import { useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SkillTestBuilder } from '../../components/employer/SkillTestBuilder';
import type { SkillTestDraft } from '../../types/skillTests';
import { useSkillTests } from '../../hooks/useSkillTests';
import { toast } from 'sonner';

export function SkillTestEditPage() {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const { getTestById, updateTest } = useSkillTests();
  const numericId = Number(testId);
  const test = Number.isNaN(numericId) ? undefined : getTestById(numericId);

  const initialDraft = useMemo<SkillTestDraft | null>(() => {
    if (!test) {
      return null;
    }
    return {
      title: test.title,
      description: test.description,
      category: test.category,
      duration: test.duration,
      passingScore: test.passingScore,
      questions: test.questions,
      status: test.status,
    };
  }, [test]);

  const handleCancel = () => {
    navigate('/employer/dashboard', { state: { initialTab: 'skill-tests' } });
  };

  if (!test || !initialDraft) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#060713] px-4" aria-labelledby="skill-test-missing-title">
        <div className="max-w-lg rounded-xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <h1 id="skill-test-missing-title" className="text-3xl font-semibold text-white">Skill test not found</h1>
          <p className="mt-3 text-sm text-gray-400">
            The assessment you are trying to edit may have been deleted or the link is incorrect.
          </p>
          <Button
            className="mt-6 bg-gold-gradient text-black hover:brightness-110"
            onClick={handleCancel}
          >
            Back to dashboard
          </Button>
        </div>
      </main>
    );
  }

  const handleSubmit = async (draft: SkillTestDraft) => {
    const updated = await updateTest(test.id, draft);
    if (updated) {
      toast.success('Skill test updated', {
        description: `${updated.title} has been saved.`,
      });
    }
    navigate('/employer/dashboard', { state: { initialTab: 'skill-tests' } });
  };

  return (
    <main className="min-h-screen bg-[#070917] pb-24" aria-labelledby="skill-test-edit-title">
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.35em] text-gray-500">Assessments</p>
              <h1 id="skill-test-edit-title" className="text-3xl font-semibold text-white sm:text-4xl">Edit skill test</h1>
              <p className="text-sm text-gray-400">
                Update the questions, scoring, and guidance for the <span className="text-white">{test.title}</span> assessment.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 text-sm text-gray-300">
              <Badge className="w-fit bg-white/10 text-gray-200 capitalize">{test.status}</Badge>
              <span>Last modified {test.lastModified}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={handleCancel}>
              Back to dashboard
            </Button>
            <Button asChild variant="ghost" className="text-gray-300 hover:text-[#f7ef8a] hover:bg-[#ba9036]/10">
              <Link to="/employer/dashboard">View analytics</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <SkillTestBuilder mode="edit" initialValue={initialDraft} onSubmit={handleSubmit} onCancel={handleCancel} />
      </main>
    </main>
  );
}
